import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as TuroLogoFull } from 'assets/turo-logo-full-v2.svg';
import { ReactComponent as TuroLogo } from 'assets/turo-logo-v2.svg';
import { NavListItem } from 'components/sidebar/components/NavListItem';
import { navItems } from './navItems';
import { useUser } from 'store/user/hooks/useUser';
import { RoutePath } from 'router/routes/routePath';
import { ReactComponent as RightArrow } from 'assets/icons/arrow-right-icon.svg';
import { useSidebar } from 'providers/SidebarProvider';

const SSidebarWrapper = styled.nav<{ isCollapsed: boolean }>`
    flex-basis: ${({ isCollapsed }) => (isCollapsed ? '64px' : '264px')};
    flex-shrink: 0;
    background: ${({ theme }) => theme.colors.revolver};
    transition: flex-basis 0.3s ease;
    z-index: ${({ theme }) => theme.z_index.dropdown + 1};
`;

const STopSectionLink = styled(Link)<{ isCollapsed: boolean }>`
    height: ${({ theme }) => theme.height.navbar};
    border-bottom: solid 1px ${({ theme }) => theme.colors.gray};
    display: flex;
    align-items: center;
    justify-content: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'space-between')};
    padding: ${({ isCollapsed }) => (isCollapsed ? '0 12px' : '0 24px')};
    transition:
        justify-content 0.3s ease,
        padding 0.3s ease;
`;

const STuroLogoFull = styled(TuroLogoFull)`
    width: 120px;
    height: auto;
`;

const STuroLogoCollapsed = styled(TuroLogo)`
    width: 36px;
    height: auto;
`;

const SNav = styled.nav<{ isCollapsed: boolean }>`
    text-align: left;
    opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
    visibility: ${({ isCollapsed }) => (isCollapsed ? 'hidden' : 'visible')};
    width: ${({ isCollapsed }) => (isCollapsed ? '0' : 'auto')};
    overflow: ${({ isCollapsed }) => (isCollapsed ? 'hidden' : 'visible')};
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease,
        width 0.3s ease;
`;

const SNavDescription = styled.p`
    margin: 0;
    padding: 27px 24px 8px;
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.semi_bold};
    letter-spacing: 2.2px;
    color: ${({ theme }) => theme.colors.gray};
    text-transform: uppercase;
`;

const SNavList = styled.ul`
    display: flex;
    flex-direction: column;
    list-style: none;
    padding: 0;
    margin: 0;
`;

const SCollapseButton = styled.button<{ isCollapsed: boolean }>`
    position: absolute;
    bottom: 24px;
    right: ${({ isCollapsed }) => (isCollapsed ? '50%' : '24px')};
    transform: ${({ isCollapsed }) =>
        isCollapsed ? 'translateX(50%) rotate(180deg)' : 'rotate(0deg)'};
    background: ${({ theme }) => theme.colors.gray};
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: ${({ theme }) => theme.colors.light_gray};
    }

    svg {
        width: 16px;
        height: 16px;
        color: ${({ theme }) => theme.colors.white};
    }
`;

const SSidebarContainer = styled.div`
    position: relative;
    height: 100vh;
    width: 100%;
`;

export const Sidebar = () => {
    const { t } = useTranslation();
    const { pathname: currentPath } = useLocation();
    const {
        state: {
            user: { isAdmin },
        },
    } = useUser();

    const { isCollapsed, toggleCollapse } = useSidebar();

    return (
        <SSidebarWrapper isCollapsed={isCollapsed}>
            <SSidebarContainer>
                <STopSectionLink
                    to={RoutePath.Structures}
                    aria-label={t('navbar.structures')}
                    isCollapsed={isCollapsed}>
                    {isCollapsed ? <STuroLogoCollapsed /> : <STuroLogoFull />}
                </STopSectionLink>
                <SNav isCollapsed={isCollapsed}>
                    <SNavDescription>{t('sidebar.description')}</SNavDescription>
                    <SNavList>
                        {isAdmin ? (
                            navItems.map(({ label, path, pathRoot }) => (
                                <NavListItem
                                    key={path}
                                    active={currentPath === path || currentPath.includes(pathRoot)}
                                    label={t(label)}
                                    path={path}
                                />
                            ))
                        ) : (
                            <NavListItem
                                active={
                                    currentPath === navItems[0].path ||
                                    currentPath.includes(navItems[0].pathRoot)
                                }
                                label={t(navItems[0].label)}
                                path={navItems[0].path}
                            />
                        )}
                    </SNavList>
                </SNav>
                <SCollapseButton
                    onClick={toggleCollapse}
                    isCollapsed={isCollapsed}
                    aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}>
                    <RightArrow />
                </SCollapseButton>
            </SSidebarContainer>
        </SSidebarWrapper>
    );
};
